
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    CustomerContact, CustomerContactModule, CustomerContactProp,
} from "../../models/customers/CustomerContact";
import { ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {CustomerId} from "../../models/customers/Customer";
import {ContactId} from "../../models/common/Contact";

/**
 * base API path to interact with project roles
 */
function apiPath( customerContactId: number, id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "customers/"+ customerContactId +"/contacts/" + ( id !== null ? id : '');
}

/** union of all project role actions types */
export type CustomerContactActionTypes = FetchCustomerContactsActionTypes
    | FetchCustomerContactByIdActionTypes
    | CreateCustomerContactActionTypes
    | UpdateCustomerContactActionTypes
    | DeleteCustomerContactActionTypes
    | RestoreCustomerContactActionTypes

/**
 * FETCH CUSTOMER_CONTACT CONTACTS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchCustomerContactsAction extends CustomerId, ViewIdentifiable, ReduxAction< typeof FETCH_CUSTOMER_CONTACTS > {}
export interface FetchCustomerContactsSuccessAction extends CustomerId, ViewIdentifiable, ReduxAction<typeof FETCH_CUSTOMER_CONTACTS_SUCCESS > {
    payload: ApiReturnType< CustomerContact[] >
}
export interface FetchCustomerContactsErrorAction extends CustomerId, ViewIdentifiable, ErrorAction< typeof FETCH_CUSTOMER_CONTACTS_ERROR >{}

export type FetchCustomerContactsActionTypes = FetchCustomerContactsAction
    | FetchCustomerContactsErrorAction
    | FetchCustomerContactsSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMER_CONTACTS = "FETCH_CUSTOMER_CONTACTS";
export const FETCH_CUSTOMER_CONTACTS_SUCCESS = "FETCH_CUSTOMER_CONTACTS_SUCCESS";
export const FETCH_CUSTOMER_CONTACTS_ERROR = "FETCH_CUSTOMER_CONTACTS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchCustomerContacts(
    customerId: Identifier<number>,
    viewIdentifier: string
) : FetchCustomerContactsAction
{
    return { type: FETCH_CUSTOMER_CONTACTS, viewIdentifier, customerId };
}
export function fetchCustomerContactsSuccess(
    customerId: Identifier<number>,
    viewIdentifier: string,
    payload: ApiReturnType< CustomerContact[] >
) : FetchCustomerContactsSuccessAction
{
    return { type: FETCH_CUSTOMER_CONTACTS_SUCCESS, viewIdentifier, payload, customerId };
}
export function fetchCustomerContactsError(
    customerId: Identifier<number>,
    viewIdentifier: string,
    error: Error
): FetchCustomerContactsErrorAction
{
    return { type: FETCH_CUSTOMER_CONTACTS_ERROR, viewIdentifier, error, customerId };
}

// REDUX OBSERVABLE EPIC
export function fetchCustomerContactsEpic(
    action$: Observable< FetchCustomerContactsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMER_CONTACTS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath( action.customerId.rawValue ),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchCustomerContactsSuccess(
                    action.customerId,
                    action.viewIdentifier,
                    response.response as ApiReturnType< CustomerContact[] >
                ) ),
                catchError(error => of(
                    fetchCustomerContactsError(
                        action.customerId,
                        action.viewIdentifier,
                        error
                    ),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchCustomerContacts")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE CUSTOMER_CONTACT
 */
export interface FetchCustomerContactByIdAction extends CustomerId, ContactId, ReduxAction< typeof FETCH_CUSTOMER_CONTACT_BY_ID> {

}
export interface FetchCustomerContactByIdSuccessAction extends CustomerContactProp, ReduxAction< typeof FETCH_CUSTOMER_CONTACT_BY_ID_SUCCESS> {

}
export interface FetchCustomerContactByIdErrorAction extends ContactId, CustomerId, ErrorAction< typeof FETCH_CUSTOMER_CONTACT_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchCustomerContactByIdActionTypes = FetchCustomerContactByIdAction
    | FetchCustomerContactByIdSuccessAction
    | FetchCustomerContactByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMER_CONTACT_BY_ID = "FETCH_CUSTOMER_CONTACT_BY_ID";
export const FETCH_CUSTOMER_CONTACT_BY_ID_SUCCESS = "FETCH_CUSTOMER_CONTACT_BY_ID_SUCCESS";
export const FETCH_CUSTOMER_CONTACT_BY_ID_ERROR = "FETCH_CUSTOMER_CONTACT_BY_ID_ERROR";

//Redux action accessors function
export function fetchCustomerContactById(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): FetchCustomerContactByIdAction {
    return { type: FETCH_CUSTOMER_CONTACT_BY_ID, contactId, customerId };
}
export function fetchCustomerContactByIdSuccess( customerContact: CustomerContact ): FetchCustomerContactByIdSuccessAction {
    return { type: FETCH_CUSTOMER_CONTACT_BY_ID_SUCCESS, customerContact };
}
export function fetchCustomerContactByIdError(
    customerId: Identifier<number>,
    contactId: Identifier<number>,
    error: any,
): FetchCustomerContactByIdErrorAction {
    return { type: FETCH_CUSTOMER_CONTACT_BY_ID_ERROR, error, contactId, customerId };
}

//redux observable epic as bridge to rxjs
export function fetchCustomerContactByIdEpic(
    action$: Observable< FetchCustomerContactByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMER_CONTACT_BY_ID ),
        switchMap( action => {
                return ajax(
                    APP_CONFIG.getRequestObject(
                        apiPath(
                            action.customerId.rawValue,
                            action.contactId.rawValue
                        )
                    )
                ).pipe(
                    map( response => fetchCustomerContactByIdSuccess( response.response as CustomerContact ) ),
                    catchError( error => of(
                        fetchCustomerContactByIdError( action.customerId, action.contactId, error ),
                        eventNotificationError( error, store$, CustomerContactModule.identifier, "fetchCustomerContactById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * successful mutating requests should be send back to the detail page
 */
function sendUserToPageURL( customerId: number ) {
    if( typeof RouteConfig.customers.routes.detail.link !== 'undefined' )
        return RouteConfig.customers.routes.detail.link( customerId );
    else
        return RouteConfig.customers.routes.main.path;
}

/**
 * CREATE CUSTOMER_CONTACT
 */
export interface CreateCustomerContactAction extends CustomerId, CustomerContactProp, ReduxAction<typeof CREATE_CUSTOMER_CONTACT> {

}
export interface CreateCustomerContactSuccessAction extends CustomerId, ContactId, ReduxAction<typeof CREATE_CUSTOMER_CONTACT_SUCCESS> {

}
export interface CreateCustomerContactErrorAction extends CustomerId, ErrorAction<typeof CREATE_CUSTOMER_CONTACT_ERROR> {

}
// typescript union type of create actions
export type CreateCustomerContactActionTypes = CreateCustomerContactAction
    | CreateCustomerContactSuccessAction
    | CreateCustomerContactErrorAction

//redux action constants
export const CREATE_CUSTOMER_CONTACT = "CREATE_CUSTOMER_CONTACT";
export const CREATE_CUSTOMER_CONTACT_SUCCESS = "CREATE_CUSTOMER_CONTACT_SUCCESS";
export const CREATE_CUSTOMER_CONTACT_ERROR = "CREATE_CUSTOMER_CONTACT_ERROR";

//redux action accessors functions
export function createCustomerContact( customerContact: CustomerContact ): CreateCustomerContactAction {
    return { type: CREATE_CUSTOMER_CONTACT, customerContact, customerId: customerContact.customerId };
}
export function createCustomerContactSuccess(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): CreateCustomerContactSuccessAction
{
    return { type: CREATE_CUSTOMER_CONTACT_SUCCESS, contactId, customerId };
}
export function createCustomerContactError(
    customerId: Identifier< number>,
    error: any
): CreateCustomerContactErrorAction
{
    return { type: CREATE_CUSTOMER_CONTACT_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function createCustomerContactEpic(
    action$: Observable< CreateCustomerContactAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_CUSTOMER_CONTACT ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.customerContact.customerId.rawValue ),
                    RouteMethods.post,
                    {
                        ...action.customerContact,
                        customerId: action.customerContact.customerId
                    }
                )
            ).pipe(
                switchMap( _ => of(
                    createCustomerContactSuccess(
                        action.customerContact.customerId,
                        action.customerContact.contactId
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        sendUserToPageURL( action.customerContact.customerId.rawValue ),
                        CustomerContactModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createCustomerContactError( action.customerContact.customerId, error ),
                    eventNotificationError( error, store$, CustomerContactModule.identifier, "createCustomerContact" )
                ))
            )
        )
    )
}

/**
 * UPDATE CUSTOMER_CONTACT
 */
export interface UpdateCustomerContactAction extends CustomerId, CustomerContactProp, ReduxAction<typeof UPDATE_CUSTOMER_CONTACT> {

}
export interface UpdateCustomerContactSuccessAction extends CustomerId, ContactId, ReduxAction<typeof UPDATE_CUSTOMER_CONTACT_SUCCESS> {

}
export interface UpdateCustomerContactErrorAction extends CustomerId, ErrorAction<typeof UPDATE_CUSTOMER_CONTACT_ERROR> {

}
// typescript union type of update actions
export type UpdateCustomerContactActionTypes = UpdateCustomerContactAction
    | UpdateCustomerContactSuccessAction
    | UpdateCustomerContactErrorAction

//redux action constants
export const UPDATE_CUSTOMER_CONTACT = "UPDATE_CUSTOMER_CONTACT";
export const UPDATE_CUSTOMER_CONTACT_SUCCESS = "UPDATE_CUSTOMER_CONTACT_SUCCESS";
export const UPDATE_CUSTOMER_CONTACT_ERROR = "UPDATE_CUSTOMER_CONTACT_ERROR";

//redux action accessors functions
export function updateCustomerContact( customerContact: CustomerContact ): UpdateCustomerContactAction {
    return { type: UPDATE_CUSTOMER_CONTACT, customerContact, customerId: customerContact.customerId };
}
export function updateCustomerContactSuccess(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): UpdateCustomerContactSuccessAction {
    return { type: UPDATE_CUSTOMER_CONTACT_SUCCESS,contactId, customerId };
}
export function updateCustomerContactError(
    customerId: Identifier<number>,
    error: any
): UpdateCustomerContactErrorAction {
    return { type: UPDATE_CUSTOMER_CONTACT_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function updateCustomerContactEpic(
    action$: Observable< UpdateCustomerContactAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_CUSTOMER_CONTACT ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerContact.customerId.rawValue,
                        action.customerContact.contactId.rawValue
                    ),
                    RouteMethods.put,
                    action.customerContact
                )
            ).pipe(
                switchMap( _ => of(
                    updateCustomerContactSuccess(
                        action.customerContact.customerId,
                        action.customerContact.contactId
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        sendUserToPageURL( action.customerContact.customerId.rawValue ),
                        CustomerContactModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateCustomerContactError( action.customerContact.customerId, error ),
                    eventNotificationError( error, store$, CustomerContactModule.identifier, "updateCustomerContact" )
                ))
            )
        )
    )
}

/**
 * DELETE CUSTOMER_CONTACT
 */
export interface DeleteCustomerContactAction extends CustomerId, ContactId, ReduxAction<typeof DELETE_CUSTOMER_CONTACT> {

}
export interface DeleteCustomerContactSuccessAction extends CustomerId, ContactId, ReduxAction<typeof DELETE_CUSTOMER_CONTACT_SUCCESS> {

}
export interface DeleteCustomerContactErrorAction extends CustomerId, ErrorAction<typeof DELETE_CUSTOMER_CONTACT_ERROR> {

}
// typescript union type of delete actions
export type DeleteCustomerContactActionTypes = DeleteCustomerContactAction
    | DeleteCustomerContactSuccessAction
    | DeleteCustomerContactErrorAction

//redux action constants
export const DELETE_CUSTOMER_CONTACT = "DELETE_CUSTOMER_CONTACT";
export const DELETE_CUSTOMER_CONTACT_SUCCESS = "DELETE_CUSTOMER_CONTACT_SUCCESS";
export const DELETE_CUSTOMER_CONTACT_ERROR = "DELETE_CUSTOMER_CONTACT_ERROR";

//redux action accessors functions
export function deleteCustomerContact(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): DeleteCustomerContactAction {
    return { type: DELETE_CUSTOMER_CONTACT, contactId, customerId };
}
export function deleteCustomerContactSuccess(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): DeleteCustomerContactSuccessAction {
    return { type: DELETE_CUSTOMER_CONTACT_SUCCESS, contactId, customerId };
}
export function deleteCustomerContactError( customerId: Identifier< number >, error: any ): DeleteCustomerContactErrorAction {
    return { type: DELETE_CUSTOMER_CONTACT_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function deleteCustomerContactEpic(
    action$: Observable< DeleteCustomerContactAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_CUSTOMER_CONTACT ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerId.rawValue,
                        action.contactId.rawValue
                    ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteCustomerContactSuccess( action.customerId, action.contactId ),
                    eventNotificationSuccess(),
                    fetchCustomerContactById( action.customerId, action.contactId )
                ) ),
                catchError( error => of(
                    deleteCustomerContactError( action.customerId, error ),
                    eventNotificationError( error, store$, CustomerContactModule.identifier, "deleteCustomerContact" )
                ))
            )
        )
    )
}

/**
 * RESTORE CUSTOMER_CONTACT
 */
export interface RestoreCustomerContactAction extends CustomerId, ContactId, ReduxAction<typeof RESTORE_CUSTOMER_CONTACT> {

}
export interface RestoreCustomerContactSuccessAction extends CustomerId, ContactId, ReduxAction<typeof RESTORE_CUSTOMER_CONTACT_SUCCESS> {

}
export interface RestoreCustomerContactErrorAction extends CustomerId, ErrorAction<typeof RESTORE_CUSTOMER_CONTACT_ERROR> {

}
// typescript union type of restore actions
export type RestoreCustomerContactActionTypes = RestoreCustomerContactAction
    | RestoreCustomerContactSuccessAction
    | RestoreCustomerContactErrorAction

//redux action constants
export const RESTORE_CUSTOMER_CONTACT = "RESTORE_CUSTOMER_CONTACT";
export const RESTORE_CUSTOMER_CONTACT_SUCCESS = "RESTORE_CUSTOMER_CONTACT_SUCCESS";
export const RESTORE_CUSTOMER_CONTACT_ERROR = "RESTORE_CUSTOMER_CONTACT_ERROR";

//redux action accessors functions
export function restoreCustomerContact(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): RestoreCustomerContactAction {
    return { type: RESTORE_CUSTOMER_CONTACT, contactId, customerId };
}
export function restoreCustomerContactSuccess(
    customerId: Identifier<number>,
    contactId: Identifier<number>
): RestoreCustomerContactSuccessAction {
    return { type: RESTORE_CUSTOMER_CONTACT_SUCCESS, contactId, customerId };
}
export function restoreCustomerContactError(
    customerId: Identifier<number>,
    error: any
): RestoreCustomerContactErrorAction {
    return { type: RESTORE_CUSTOMER_CONTACT_ERROR, error, customerId };
}

//redux observable rxjs bridge
export function restoreCustomerContactEpic(
    action$: Observable< RestoreCustomerContactAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_CUSTOMER_CONTACT ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(
                        action.customerId.rawValue,
                        action.contactId.rawValue
                    ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreCustomerContactSuccess( action.customerId, action.contactId ),
                    eventNotificationSuccess(),
                    fetchCustomerContactById( action.customerId, action.contactId )
                ) ),
                catchError( error => of(
                    restoreCustomerContactError( action.customerId, error ),
                    eventNotificationError( error, store$, CustomerContactModule.identifier, "restoreCustomerContact" )
                ))
            )
        )
    )
}